import { TextField, TranslateVar } from "@faro-lotv/flat-ui";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";

/**
 * Converts an array of selected page numbers into a compact string representation of ranges.
 *
 * Consecutive pages are grouped into ranges (e.g., "1-3"), while non-consecutive pages are listed separately (e.g., "1, 3, 5-7").
 * The input array does not need to be sorted; the function will sort it internally.
 * E.g:  The following input array ([1, 2, 3, 5, 7, 8, 9]) will give the following string:"1-3, 5, 7-9"
 *
 * @param selectedPages - An array of page numbers to be converted into a range string.
 * @returns A string representing the selected pages as ranges, or an empty string if no pages are selected.
 */
export function getPagesRangeFromSelectedPages(
  selectedPages: number[],
): string {
  if (selectedPages.length === 0) return "";
  if (selectedPages.length === 1) return `${selectedPages[0]}`;
  // Ensure that the pages are unique
  const uniquePages = Array.from(new Set(selectedPages));
  // Sort pages to ensure correct order
  const sortedPages = [...uniquePages].sort((a, b) => a - b);
  const ranges: string[] = [];
  let rangeStart = sortedPages[0];
  let rangeEnd = sortedPages[0];

  for (let i = 1; i < sortedPages.length; i++) {
    if (sortedPages[i] === rangeEnd + 1) {
      // Consecutive page, extend current range
      rangeEnd = sortedPages[i];
    } else {
      // Non-consecutive page, finalize current range and start new one
      if (rangeStart === rangeEnd) {
        ranges.push(`${rangeStart}`);
      } else {
        ranges.push(`${rangeStart}-${rangeEnd}`);
      }
      rangeStart = sortedPages[i];
      rangeEnd = sortedPages[i];
    }
  }

  // Add the final range
  if (rangeStart === rangeEnd) {
    ranges.push(`${rangeStart}`);
  } else {
    ranges.push(`${rangeStart}-${rangeEnd}`);
  }

  return ranges.join(", ");
}

/** Represents the result of parsing selected pages from ranges. */
type SelectedPagesFromRangesResult =
  | {
      /** Indicates whether the parsing was successful */
      success: true;
      /** The array of selected page numbers */
      selectedPages: number[];
    }
  | {
      /** Indicates whether the parsing was successful */
      success: false;
      /** The error message to display */
      error: React.ReactNode;
    };
/**
 * Parses a string representing page ranges and returns an array of selected page numbers.
 *
 * The input string can contain individual page numbers and/or ranges separated by commas.
 * Ranges are specified with a hyphen (e.g., "1-3,5,7-9" results in [1,2,3,5,7,8,9]).
 * All page numbers must be within the bounds of 1 and `totalNbOfPages` (inclusive).
 *
 * @param ranges - A string containing page numbers and/or ranges (e.g., "1-3,5,7-9").
 * @param totalNbOfPages - The total number of pages available for selection.
 * @returns An array of selected page numbers, in the order they appear in the input.
 * @throws Will throw an error if any part of the input is not a valid number or is out of bounds.
 */
export function getSelectedPagesFromRanges(
  ranges: string,
  totalNbOfPages: number,
): SelectedPagesFromRangesResult {
  const uniquePages = new Set<number>();
  const parts = ranges.split(",").map((part) => part.trim());
  // An empty selection should not result in an error
  if (parts.length === 0 || (parts.length === 1 && parts[0] === "")) {
    return { success: true, selectedPages: [] };
  }

  for (const part of parts) {
    if (part.includes("-")) {
      const rangeBounds = part.split("-");
      if (rangeBounds.length !== 2) {
        return {
          success: false,
          error: "Only one hyphen (-) allowed by range",
        };
      }
      const [start, end] = rangeBounds.map((bound) => bound.trim());
      if (start === "") {
        return { success: false, error: "Missing starting page number" };
      }
      if (end === "") {
        return { success: false, error: "Missing ending page number" };
      }
      const startNumber = Number(start);
      if (isNaN(startNumber)) {
        return {
          success: false,
          error: (
            <>
              Start page value (
              <TranslateVar name="start page value">{start}</TranslateVar>)
              should be a number in the range "
              <TranslateVar name="range">{part}</TranslateVar>"
            </>
          ),
        };
      }
      const endNumber = Number(end);
      if (isNaN(endNumber)) {
        return {
          success: false,
          error: (
            <>
              End page value (
              <TranslateVar name="end page value">{end}</TranslateVar>) should
              be a number in the range "
              <TranslateVar name="range">{part}</TranslateVar>"
            </>
          ),
        };
      }
      if (startNumber >= endNumber) {
        return {
          success: false,
          error: (
            <>
              Start page number (
              <TranslateVar name="start page number">
                {startNumber}
              </TranslateVar>
              ) must be less than end page number (
              <TranslateVar name="end page number">{endNumber}</TranslateVar>)
            </>
          ),
        };
      }
      if (startNumber < 1 || startNumber > totalNbOfPages) {
        return {
          success: false,
          error: (
            <>
              Start page number (
              <TranslateVar name="start page number">
                {startNumber}
              </TranslateVar>
              ) is out of bounds, limit is{" "}
              <TranslateVar name="total pages">{totalNbOfPages}</TranslateVar>
            </>
          ),
        };
      }
      if (endNumber < 1 || endNumber > totalNbOfPages) {
        return {
          success: false,
          error: (
            <>
              End page number (
              <TranslateVar name="end page number">{endNumber}</TranslateVar>)
              is out of bounds, limit is{" "}
              <TranslateVar name="total pages">{totalNbOfPages}</TranslateVar>
            </>
          ),
        };
      }
      for (let i = startNumber; i <= endNumber; i++) {
        uniquePages.add(i);
      }
      if (startNumber < 1 || startNumber > totalNbOfPages) {
        return {
          success: false,
          error: (
            <>
              <TranslateVar name="start page number">
                {startNumber}
              </TranslateVar>{" "}
              is out of bounds, limit is{" "}
              <TranslateVar name="total pages">{totalNbOfPages}</TranslateVar>
            </>
          ),
        };
      }
      if (endNumber < 1 || endNumber > totalNbOfPages) {
        return {
          success: false,
          error: (
            <>
              <TranslateVar name="end page number">{endNumber}</TranslateVar> is
              out of bounds, limit is{" "}
              <TranslateVar name="total pages">{totalNbOfPages}</TranslateVar>
            </>
          ),
        };
      }
      for (let i = startNumber; i <= endNumber; i++) {
        uniquePages.add(i);
      }
    } else {
      if (part.trim() === "") {
        return { success: false, error: "Add page number or remove comma" };
      }
      const pageNumber = Number(part);
      if (isNaN(pageNumber)) {
        return {
          success: false,
          error: (
            <>
              Page value (<TranslateVar name="page value">{part}</TranslateVar>)
              is not a valid number
            </>
          ),
        };
      }
      if (pageNumber < 1 || pageNumber > totalNbOfPages) {
        return {
          success: false,
          error: (
            <>
              Page number (
              <TranslateVar name="page number">{pageNumber}</TranslateVar>) is
              out of bounds, limit is{" "}
              <TranslateVar name="total pages">{totalNbOfPages}</TranslateVar>
            </>
          ),
        };
      }
      uniquePages.add(pageNumber);
    }
  }
  return { success: true, selectedPages: Array.from(uniquePages) };
}

export type PageSelectorTextFieldProps = {
  totalNbOfPages: number;
  selectedPages?: number[];
  onSelect(pages?: number[]): void;
};

/**
 * A text field component for selecting pages by entering ranges or individual page numbers.
 *
 * @param totalNbOfPages - The total number of available pages.
 * @param selectedPages - The currently selected pages as an array of numbers.
 * @param onSelect - Callback invoked when the selection changes, receiving the new selected pages or `undefined` if the input is invalid.
 * @returns The rendered text field component for selecting pages.
 *
 * - Displays the current selection as a range string (e.g., "1-3,5").
 * - Parses user input and updates the selection, showing an error message if the input is invalid.
 * - Keeps the text field in sync with external changes to `selectedPages`.
 */
export function PageSelectorTextField({
  totalNbOfPages,
  selectedPages,
  onSelect,
}: PageSelectorTextFieldProps): JSX.Element {
  const [selectionText, setSelectionText] = useState("");
  const [errorText, setErrorText] = useState<React.ReactNode>("");
  const selectionChanged = useRef(false);

  useEffect(() => {
    if (selectedPages) {
      if (!selectionChanged.current) {
        const newText = getPagesRangeFromSelectedPages(selectedPages);
        setSelectionText(newText);
      }
      selectionChanged.current = false;
    }
  }, [selectedPages]);

  const handleTextChange = useCallback(
    (newText: string) => {
      selectionChanged.current = true;
      setSelectionText(newText);
      const selectionResult = getSelectedPagesFromRanges(
        newText,
        totalNbOfPages,
      );
      if (selectionResult.success) {
        setErrorText("");
        onSelect(selectionResult.selectedPages);
      } else {
        setErrorText(selectionResult.error);
        onSelect(undefined);
      }
    },
    [totalNbOfPages, onSelect],
  );

  return (
    <TextField
      label="Selection"
      helpText={
        <>
          {"e.g: "}{" "}
          <TranslateVar name="page-range-example">1-3, 5, 7-9</TranslateVar>
        </>
      }
      text={selectionText}
      error={errorText}
      onTextChanged={handleTextChange}
      sx={{
        height: "2rem",
      }}
    />
  );
}
